import { isJust, isNothing, just, nothing, type Maybe } from "./maybe";
import { normalizeAssetSrc } from "./projectAssets";
import {
  readProjectAssetDataUrl,
  writeProjectAssetBytes,
} from "./tauri/assets";

function mimeForAssetPath(relativePath: string): string {
  const ext = relativePath.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    default:
      return "image/jpeg";
  }
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = dataUrl;
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to encode image"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("Failed to encode image"));
    reader.readAsDataURL(blob);
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read image"));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
): Promise<Blob> {
  const blob = await new Promise<Maybe<Blob>>((resolve) => {
    canvas.toBlob(
      (value) => resolve(value === null ? nothing : just(value)),
      mime,
      0.92,
    );
  });
  if (isJust(blob)) return blob.value;
  if (mime !== "image/png") {
    const png = await new Promise<Maybe<Blob>>((resolve) => {
      canvas.toBlob(
        (value) => resolve(value === null ? nothing : just(value)),
        "image/png",
      );
    });
    if (isJust(png)) return png.value;
  }
  throw new Error(`Could not encode image as ${mime}`);
}

async function writeCanvasAsset(
  canvas: HTMLCanvasElement,
  storageRoot: string,
  relativePath: string,
): Promise<void> {
  const path = normalizeAssetSrc(relativePath);
  const mime = mimeForAssetPath(path);
  const blob = await canvasToBlob(canvas, mime);
  const encoded = await blobToBase64(blob);
  await writeProjectAssetBytes(storageRoot, path, encoded);
}

/** Read an asset as a data URL, falling back to the live preview URL. */
export async function readAssetDataUrl(
  storageRoot: string,
  relativePath: string,
  previewSrc: Maybe<string> = nothing,
): Promise<string> {
  const path = normalizeAssetSrc(relativePath);
  try {
    return await readProjectAssetDataUrl(storageRoot, path);
  } catch (primary) {
    if (isNothing(previewSrc) || previewSrc.value.length === 0) throw primary;
    try {
      const response = await fetch(previewSrc.value);
      if (response.ok === false) throw primary;
      return await blobToDataUrl(await response.blob());
    } catch {
      throw primary;
    }
  }
}

export async function cropProjectAsset(
  storageRoot: string,
  relativePath: string,
  rect: { x: number; y: number; width: number; height: number },
  previewSrc: Maybe<string> = nothing,
): Promise<void> {
  const path = normalizeAssetSrc(relativePath);
  const dataUrl = await readAssetDataUrl(storageRoot, path, previewSrc);
  const img = await loadImage(dataUrl);
  if (img.naturalWidth === 0 || img.naturalHeight === 0) {
    throw new Error("Image has no readable dimensions.");
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(rect.width));
  canvas.height = Math.max(1, Math.round(rect.height));
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("Canvas unavailable");
  ctx.drawImage(
    img,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  await writeCanvasAsset(canvas, storageRoot, path);
}

/** Rotate a project asset 90° clockwise, preserving its file format. */
export async function rotateProjectAsset(
  storageRoot: string,
  relativePath: string,
  previewSrc: Maybe<string> = nothing,
): Promise<void> {
  const path = normalizeAssetSrc(relativePath);
  const dataUrl = await readAssetDataUrl(storageRoot, path, previewSrc);
  const img = await loadImage(dataUrl);
  if (img.naturalWidth === 0 || img.naturalHeight === 0) {
    throw new Error("Image has no readable dimensions.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalHeight;
  canvas.height = img.naturalWidth;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("Canvas unavailable");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  await writeCanvasAsset(canvas, storageRoot, path);
}
